import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Link2, UploadCloud, Printer, Play, Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import VisionDeck from "./VisionDeck";
import "./vision.css";

export default function VisionInternal() {
  const [d, setD] = useState(null);
  const [live, setLive] = useState(null);
  const [publiserer, setPubliserer] = useState(false);
  const [visInnstillinger, setVisInnstillinger] = useState(false);
  const [kontakter, setKontakter] = useState([]);
  const [mottaker, setMottaker] = useState("");
  const [rolle, setRolle] = useState("");
  const nav = useNavigate();
  const aktor = localStorage.getItem("nas_aktor") || "Admin";

  const load = useCallback(async () => {
    const [{ data: v }, { data: o }] = await Promise.all([
      api.get("/admin/vision"),
      api.get("/admin/os/overview"),
    ]);
    setD(v);
    setLive(o);
    const partner = v.links.find((l) => l.view === "partner");
    setMottaker(partner?.recipient || "");
    setRolle(partner?.recommendedRole || "");
  }, []);

  useEffect(() => {
    document.title = "Vision — Norwegian AI Summit";
    load().catch(() => toast.error("Kunne ikke laste vision-data"));
  }, [load]);

  const kopier = (lenke) => {
    const url = `${window.location.origin}/vision/s/${lenke.token}`;
    navigator.clipboard.writeText(url);
    if (lenke.passcode) {
      toast.success(`Partner-lenken er kopiert — passord: ${lenke.passcode}`, { duration: 8000 });
    } else {
      toast.success(`${lenke.view === "speaker" ? "Speaker" : "Partner"}-lenken er kopiert`);
    }
  };

  const publiser = async () => {
    setPubliserer(true);
    try {
      await api.post("/admin/vision/publish", { actor: aktor });
      toast.success("Eksterne decks er oppdatert med gjeldende fakta");
      await load();
    } finally {
      setPubliserer(false);
    }
  };

  const aapneInnstillinger = async () => {
    setVisInnstillinger(true);
    try {
      const { data } = await api.get("/admin/vision/kontakt");
      setKontakter(data.contacts || []);
    } catch {
      setKontakter([]);
    }
  };

  const lagreAnbefaling = async () => {
    await api.patch("/admin/vision/links/partner/anbefaling", { recipient: mottaker, recommendedRole: rolle });
    toast.success("Partner-deck anbefaling lagret");
    await load();
  };

  if (!d) {
    return (
      <div className="vis-root vis-borte" data-testid="vision-internal-laster">
        <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "#8b9199" }}>
          Loading vision …
        </p>
      </div>
    );
  }

  return (
    <div data-testid="vision-internal">
      <div className="vis-topbar">
        <button type="button" onClick={() => nav("/admin/overview")} className="vis-ctrl" data-testid="vision-til-admin">
          <ArrowLeft size={12} /> Admin
        </button>
        <span className="vis-tag" data-testid="vision-internal-tag">Internal · Vision master</span>
        {d.draftDiffers && (
          <span className="vis-tag" style={{ color: "#f1e75a", borderColor: "rgba(241,231,90,0.4)" }} data-testid="vision-draft-avvik">
            Draft differs from published
          </span>
        )}
        <span style={{ marginLeft: "auto" }} />
        {d.links.map((l) => (
          <button key={l.id} type="button" onClick={() => kopier(l)} className="vis-ctrl" data-testid={`vision-kopier-${l.view}`}>
            <Link2 size={12} /> {l.view === "speaker" ? "Speaker link" : "Partner link"}
          </button>
        ))}
        <button type="button" onClick={aapneInnstillinger} className="vis-ctrl" data-testid="vision-innstillinger">
          <Settings2 size={12} /> Partner-deck
        </button>
        <button type="button" onClick={() => window.dispatchEvent(new Event("vis-present"))} className="vis-ctrl" data-testid="vision-present-topp">
          <Play size={12} /> Present
        </button>
        <button type="button" onClick={() => window.print()} className="vis-ctrl" data-testid="vision-print">
          <Printer size={12} /> PDF
        </button>
        <button type="button" onClick={publiser} disabled={publiserer} className="vis-ctrl primar" data-testid="vision-publiser">
          <UploadCloud size={12} /> {publiserer ? "Publishing …" : "Publish external decks"}
        </button>
      </div>
      <VisionDeck view="internal" data={d.draft} live={live} calibre={d.stageCalibre} />
      {visInnstillinger && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(5, 7, 11, 0.8)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          data-testid="vision-innstillinger-modal"
          onClick={(e) => e.target === e.currentTarget && setVisInnstillinger(false)}
        >
          <div style={{ width: "min(620px, 100%)", maxHeight: "88vh", overflowY: "auto", background: "#0a0e13", border: "1px solid rgba(245,241,232,0.14)", padding: "clamp(24px, 4vw, 40px)" }}>
            <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#f1e75a" }}>
              Partner-deck
            </p>
            <h3 style={{ marginTop: 12, fontFamily: '"Switzer", sans-serif', fontWeight: 500, textTransform: "uppercase", fontSize: 24, color: "#f5f1e8" }}>
              Anbefalt rolle per mottaker
            </h3>
            <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: "#8b9199" }}>
              Vises som «Recommended for …» på siste slide i partner-decket. Tomt felt = ingen markering.
            </p>
            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <input
                value={mottaker}
                onChange={(e) => setMottaker(e.target.value)}
                placeholder="Mottaker, f.eks. DNB"
                data-testid="vision-anbefaling-mottaker"
                style={{ background: "rgba(7,10,14,0.6)", border: "1px solid rgba(245,241,232,0.18)", color: "#f5f1e8", padding: "10px 13px", fontSize: 13, outline: "none" }}
              />
              <select
                value={rolle}
                onChange={(e) => setRolle(e.target.value)}
                data-testid="vision-anbefaling-rolle"
                style={{ background: "rgba(7,10,14,0.6)", border: "1px solid rgba(245,241,232,0.18)", color: "#f5f1e8", padding: "10px 13px", fontSize: 13, outline: "none" }}
              >
                <option value="">Ingen anbefalt rolle</option>
                {["Founding Partner", "Strategic Partner", "Broadcast Partner", "Summit Dinner Partner", "AI Experience Partner"].map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <button type="button" className="vis-ctrl primar" style={{ marginTop: 14 }} onClick={lagreAnbefaling} data-testid="vision-anbefaling-lagre">
              Lagre anbefaling
            </button>
            <div style={{ marginTop: 30, borderTop: "1px solid rgba(245,241,232,0.12)", paddingTop: 20 }}>
              <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: "#8b9199" }} data-testid="vision-kontakter-tittel">
                Henvendelser ({kontakter.length})
              </p>
              {kontakter.length === 0 ? (
                <p style={{ marginTop: 12, fontSize: 12.5, color: "#565c66" }} data-testid="vision-kontakter-tom">Ingen henvendelser ennå.</p>
              ) : (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 14 }} data-testid="vision-kontakter-liste">
                  {kontakter.map((k) => (
                    <div key={k.id} style={{ borderLeft: "2px solid rgba(241,231,90,0.4)", paddingLeft: 12 }}>
                      <p style={{ fontSize: 13.5, fontWeight: 600, color: "#f5f1e8" }}>
                        {k.name}{k.company ? ` · ${k.company}` : ""}
                      </p>
                      <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 10.5, color: "#8b9199", marginTop: 2 }}>
                        {k.email} · {(k.createdAt || "").slice(0, 10)}
                      </p>
                      {k.message && <p style={{ fontSize: 12.5, color: "#a7adb5", marginTop: 6, lineHeight: 1.55 }}>{k.message}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="vis-ctrl" style={{ marginTop: 24 }} onClick={() => setVisInnstillinger(false)} data-testid="vision-innstillinger-lukk">
              Lukk
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
